import React, { useState } from 'react';
import axios from 'axios';
import { SqsPageHeader, SqsButton } from './sqs/SqsShell';
import { Mail, Send, Inbox, Sparkles, Plus } from 'lucide-react';
import SetupModal from './sqs/SetupModal';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CAMPAIGN_TYPES = [
  {
    id: 'broadcast', icon: Send, title: 'Broadcast email',
    desc: 'Send a one-time blast to your full list.',
    fields: [
      { key: 'subject', label: 'Subject line', required: true },
      { key: 'preview_text', label: 'Preview text', placeholder: 'Short preview shown in inbox' },
      { key: 'audience', label: 'Audience', type: 'select', options: ['All subscribers', 'New subscribers (30d)', 'Customers only'], required: true },
      { key: 'body', label: 'Email body', type: 'textarea', required: true },
    ],
  },
  {
    id: 'drip', icon: Sparkles, title: 'Automated drip',
    desc: 'Set up multi-step journeys triggered by behaviour.',
    fields: [
      { key: 'journey_name', label: 'Journey name', required: true },
      { key: 'trigger', label: 'Trigger', type: 'select', options: ['New subscriber', 'New customer', 'Abandoned cart', 'Birthday'], required: true },
      { key: 'num_steps', label: 'Number of steps', type: 'number', placeholder: '3', required: true },
    ],
  },
  {
    id: 'welcome', icon: Inbox, title: 'Welcome series',
    desc: 'Onboard new subscribers with a sequenced intro.',
    fields: [
      { key: 'series_name', label: 'Series name', required: true },
      { key: 'intro_subject', label: 'First-email subject', placeholder: 'Welcome!', required: true },
      { key: 'send_after', label: 'Send first email after (hours)', type: 'number', placeholder: '0' },
    ],
  },
  {
    id: 'newsletter', icon: Mail, title: 'Newsletter',
    desc: 'Recurring magazine-style sends to your subscribers.',
    fields: [
      { key: 'newsletter_name', label: 'Newsletter name', required: true },
      { key: 'frequency', label: 'Frequency', type: 'select', options: ['Weekly', 'Bi-weekly', 'Monthly'], required: true },
      { key: 'send_day', label: 'Send day', type: 'select', options: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] },
    ],
  },
];

export default function DashboardCampaigns() {
  const [active, setActive] = useState(null);
  const token = localStorage.getItem('token');

  const handleSubmit = async (typeId, values) => {
    try {
      await axios.post(`${API}/builder/campaigns/${typeId}`, values, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      const all = JSON.parse(localStorage.getItem('wb_campaigns') || '[]');
      all.push({ type: typeId, ...values, created_at: new Date().toISOString() });
      localStorage.setItem('wb_campaigns', JSON.stringify(all));
    }
    toast.success(`${active.title} created`);
  };

  return (
    <>
      <SqsPageHeader
        breadcrumbs="Campaigns"
        title="Campaigns"
        subtitle="Email marketing & customer journeys — turn one-time buyers into loyal fans."
        actions={<SqsButton variant="primary" size="sm" icon={Plus} onClick={() => setActive(CAMPAIGN_TYPES[0])} data-testid="campaigns-new">New Campaign</SqsButton>}
      />
      <div className="p-10 max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-5">
        {CAMPAIGN_TYPES.map(t => (
          <button
            key={t.id}
            onClick={() => setActive(t)}
            className="bg-[#15152E] border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.16] transition-all flex gap-4 text-left group"
            data-testid={`campaign-${t.id}`}
          >
            <div className="w-12 h-12 rounded-lg bg-[#A78BFA]/10 flex items-center justify-center shrink-0 group-hover:bg-[#A78BFA]/20 transition-all">
              <t.icon size={20} className="text-[#A78BFA]" />
            </div>
            <div className="flex-1">
              <h3 className="text-[14.5px] font-bold text-white">{t.title}</h3>
              <p className="text-[12px] text-[#9CA3AF] leading-relaxed mt-1">{t.desc}</p>
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mt-3 inline-block opacity-0 group-hover:opacity-100 transition-all">
                Set up →
              </span>
            </div>
          </button>
        ))}
      </div>

      {active && (
        <SetupModal
          open={!!active}
          onClose={() => setActive(null)}
          title={`Set up · ${active.title}`}
          icon={active.icon}
          fields={active.fields}
          submitLabel="Create campaign"
          onSubmit={(v) => handleSubmit(active.id, v)}
        />
      )}
    </>
  );
}
